import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DollarSign, Loader2, Package, Info, Scale } from "lucide-react";
import { api, formatApiErrorDetail, fmt } from "../lib/api";
import { AnimatedCounter } from "../components/AnimatedCounter";

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between border-b border-dashed border-[#E2E8F0] py-3 last:border-0">
    <span className="text-sm text-[#64748B]">{label}</span>
    <span data-testid={testId} className="text-sm font-semibold text-[#1E293B]">
      {value}
    </span>
  </div>
);

export default function Calculator() {
  const [price, setPrice] = useState("");
  const [pounds, setPounds] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const handleCalculate = async () => {
    const priceStr = price.trim();
    const poundsStr = pounds.trim();

    if (priceStr === "" && poundsStr === "") {
      setError("Ingresa un precio o un peso para calcular.");
      setResult(null);
      return;
    }

    let priceValue = null;
    if (priceStr !== "") {
      const v = parseFloat(priceStr);
      if (Number.isNaN(v)) {
        setError("Ingresa un precio válido en dólares.");
        setResult(null);
        return;
      }
      if (v < 0) {
        setError("El precio no puede ser negativo.");
        setResult(null);
        return;
      }
      priceValue = v;
    }

    let poundsValue = null;
    if (poundsStr !== "") {
      const p = parseFloat(poundsStr);
      if (Number.isNaN(p) || p < 0) {
        setError("Ingresa un peso válido en libras (o déjalo vacío).");
        setResult(null);
        return;
      }
      poundsValue = p;
    }

    if ((priceValue === null || priceValue === 0) && (poundsValue === null || poundsValue === 0)) {
      setError("Ingresa un precio o un peso mayor a cero.");
      setResult(null);
      return;
    }

    setError("");
    setLoading(true);
    try {
      const payload = {};
      if (priceValue !== null && priceValue > 0) payload.price = priceValue;
      if (poundsValue !== null && poundsValue > 0) payload.pounds = poundsValue;
      const [{ data }] = await Promise.all([
        api.post("/calculate", payload),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const includesShipping = result && result.shipping_usd > 0;
  const includesProduct = result && result.has_price;

  return (
    <div className="mx-auto w-full max-w-xl px-5 py-10 sm:py-14">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl border border-[#E2E8F0] bg-white p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        data-testid="calculator-card"
      >
        <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C9848F]">
          <Package size={14} strokeWidth={2} />
          Cotiza tu compra
        </div>
        <label htmlFor="price-input" className="mb-1 block text-base font-semibold text-[#1E293B]">
          Precio del producto (USD)
          <span className="ml-2 text-xs font-normal text-[#94A3B8]">(opcional)</span>
        </label>
        <p className="mb-3 text-xs text-[#64748B]">
          Déjalo vacío si solo quieres calcular el envío por libras.
        </p>
        <div className="relative mb-5">
          <DollarSign size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C9848F]" strokeWidth={2} />
          <input
            id="price-input"
            type="text"
            inputMode="decimal"
            value={price}
            onChange={(e) => {
              const v = e.target.value;
              if (/^\d*\.?\d*$/.test(v)) setPrice(v);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleCalculate()}
            placeholder="Ejemplo: 120.00"
            data-testid="price-input"
            className="w-full min-h-[60px] rounded-lg border border-[#E2E8F0] bg-[#FBF6F3] pl-12 pr-4 text-xl font-semibold text-[#1E293B] outline-none transition-colors placeholder:font-normal placeholder:text-[#94A3B8] focus:ring-2 focus:ring-[#C9848F] focus:border-transparent focus:bg-white"
          />
        </div>

        <label htmlFor="pounds-input" className="mb-1 block text-base font-semibold text-[#1E293B]">
          ¿Sabes el peso del producto? Calcula tus libras
          <span className="ml-2 text-xs font-normal text-[#94A3B8]">(opcional)</span>
        </label>
        <p className="mb-3 text-xs text-[#64748B]">
          Si lo agregas, sumamos el costo de envío por peso al total.
        </p>
        <div className="relative mb-5">
          <Scale size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C9848F]" strokeWidth={2} />
          <input
            id="pounds-input"
            type="text"
            inputMode="decimal"
            value={pounds}
            onChange={(e) => {
              const v = e.target.value;
              if (/^\d*\.?\d*$/.test(v)) setPounds(v);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleCalculate()}
            placeholder="Ejemplo: 2.5"
            data-testid="pounds-input"
            className="w-full min-h-[56px] rounded-lg border border-[#E2E8F0] bg-[#FBF6F3] pl-12 pr-14 text-lg font-semibold text-[#1E293B] outline-none transition-colors placeholder:font-normal placeholder:text-[#94A3B8] focus:ring-2 focus:ring-[#C9848F] focus:border-transparent focus:bg-white"
          />
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-[#64748B]">lb</span>
        </div>

        {error && (
          <p data-testid="calculator-error-message" className="mb-4 text-sm text-red-500">
            {error}
          </p>
        )}
        <button
          onClick={handleCalculate}
          disabled={loading}
          data-testid="calculate-button"
          className="w-full min-h-[56px] rounded-xl bg-[#1F2A5C] text-base font-semibold text-white shadow-lg shadow-[#1F2A5C]/30 transition-transform transition-colors transition-shadow hover:bg-[#151E45] hover:-translate-y-[1px] hover:shadow-xl hover:shadow-[#1F2A5C]/35 active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={20} className="animate-spin" /> : null}
          {loading ? "Calculando..." : "Hacer cálculo"}
        </button>
      </motion.div>

      <AnimatePresence>
        {result && (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.45 }}
            className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            data-testid="result-card"
          >
            <h2 className="mb-4 text-lg font-semibold tracking-tight text-[#1E293B]">Resultado del cálculo</h2>
            <div className="mb-6">
              {includesProduct && (
                <>
                  <Row label="Precio del producto" value={`$ ${fmt(result.price)}`} testId="result-price" />
                  <Row label={`Impuesto (${result.tax_percent}%)`} value={`$ ${fmt(result.tax)}`} testId="result-tax" />
                  <Row label={`Comisión (${result.commission_percent}%)`} value={`$ ${fmt(result.commission)}`} testId="result-commission" />
                </>
              )}
              {includesShipping && (
                <Row
                  label={`Envío por peso (${fmt(result.pounds)} lb)`}
                  value={`$ ${fmt(result.shipping_usd)}`}
                  testId="result-shipping-usd"
                />
              )}
              <Row label="Total en dólares" value={`$ ${fmt(result.total_usd)}`} testId="result-total-usd" />
              <Row label="Tasa del dólar" value={`L. ${fmt(result.exchange_rate)}`} testId="result-exchange-rate" />
            </div>
            <div
              data-testid="result-total-hnl"
              className="total-glow rounded-2xl p-6 text-center text-white"
              style={{ backgroundImage: "linear-gradient(135deg, #1F2A5C, #C9848F)" }}
            >
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80">
                Total en Lempiras
              </p>
              <p className="text-4xl sm:text-5xl font-bold tracking-tight">
                <AnimatedCounter value={result.total_hnl} prefix="L. " />
              </p>
              {includesShipping && (
                <p className="mt-2 text-xs text-white/80" data-testid="result-includes-shipping">
                  Incluye envío por peso
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {settings && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-6 flex items-start gap-3 rounded-2xl border border-[#D4A38B]/40 bg-[#D4A38B]/10 p-5"
          data-testid="pound-info-box"
        >
          <Info size={18} className="mt-0.5 shrink-0 text-[#1F2A5C]" />
          <div className="text-sm text-[#1E293B]">
            <p data-testid="price-per-pound-text">
              El precio por libra es de <span className="font-semibold">$ {fmt(settings.price_per_pound)}</span>.
            </p>
            <p data-testid="price-under-pound-text" className="text-[#64748B]">
              Si pesa menos de 1 lb el precio es <span className="font-semibold text-[#1E293B]">$ {fmt(settings.price_under_pound)}</span>.
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}
